/**
 * Customer card — the "Mokėjimai" (payments) view of the Atsiskaitymai tab.
 *
 * Shows every money movement with this counterparty and, on expand, exactly
 * which documents it was spread over. What is left unallocated IS the advance:
 * the money that has no document behind it yet, and the reason our debt used
 * to disagree with the accounting system's client balance.
 */
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Field, Label, Switch } from "@headlessui/react";
import clsx from "clsx";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import Currency from "components/format/currency/currency";
import { fetchSettlements } from "services/customer-settlements.service";
import {
  CustomerSettlementItem,
  CustomerSettlementsResponse,
} from "types/CustomerSettlements";
import { formatPrettyDate } from "utils/helperFunctions";
import { ledgerKindLabel } from "utils/ledgerKind";
import useIsPhone from "features/files/use-is-phone";
import PhoneRowCard from "./phone-row-card";

interface Props {
  companyId?: string;
  customerId?: string;
}

type TFunction = (key: string, options?: any) => string;

const PAGE_SIZE = 25;

const KIND_FILTERS: Array<{
  value: string;
  i18nKey: string;
  defaultValue: string;
}> = [
  { value: "", i18nKey: "settlement_kind_all", defaultValue: "All" },
  {
    value: "payment_in",
    i18nKey: "settlement_kind_payment_in",
    defaultValue: "Received",
  },
  {
    value: "payment_out",
    i18nKey: "settlement_kind_payment_out",
    defaultValue: "Paid",
  },
];

const cardCls =
  "rounded-xl border border-gray-200 bg-white p-4 shadow-sm flex flex-col gap-1";
const labelCls = "text-xs font-medium text-gray-500";
const valueCls = "text-lg font-semibold text-gray-900";

export const settlementKindLabel = (t: TFunction, kind: string): string => {
  if (kind === "payment_in") {
    return t("settlement_kind_payment_in", { defaultValue: "Received" });
  }
  if (kind === "payment_out") {
    return t("settlement_kind_payment_out", { defaultValue: "Paid" });
  }
  return kind;
};

export default function SettlementPayments({ companyId, customerId }: Props) {
  const { t } = useTranslation();

  const [kind, setKind] = useState<string>("");
  const [openOnly, setOpenOnly] = useState(false);
  const [page, setPage] = useState(1);

  const [data, setData] = useState<CustomerSettlementsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Same guard as the documents view: a slower earlier request must not
  // overwrite the newest response after a rapid filter change.
  const requestIdRef = useRef(0);

  const load = useCallback(
    (kindValue: string, openOnlyValue: boolean, pageValue: number) => {
      if (!companyId || !customerId) return;
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(false);
      fetchSettlements(companyId, customerId, {
        kind: kindValue || undefined,
        openOnly: openOnlyValue,
        page: pageValue,
        pageSize: PAGE_SIZE,
        // One page is 25 rows, so this is a single extra join rather than a
        // per-row fetch — the allocations are in hand before a row expands.
        withAllocations: true,
      })
        .then((response) => {
          if (requestId !== requestIdRef.current) return;
          setData(response);
          setLoading(false);
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) return;
          setError(true);
          setLoading(false);
        });
    },
    [companyId, customerId]
  );

  useEffect(() => {
    load(kind, openOnly, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, customerId, kind, openOnly, page]);

  if (loading && !data) {
    return (
      <div
        className="flex flex-col gap-5"
        data-testid="settlement-payments-skeleton"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-4">
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className={clsx(cardCls, "animate-pulse")}>
              <span className="h-3 w-20 rounded bg-gray-200" />
              <span className="h-5 w-24 rounded bg-gray-200" />
            </div>
          ))}
        </div>
        <div className="h-48 animate-pulse rounded-xl border border-gray-200 bg-gray-50" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {t("settlements_payments_load_failed", {
          defaultValue: "Could not load payments.",
        })}
        <button
          type="button"
          className="ml-3 rounded-md border border-red-300 bg-white px-3 py-1 text-red-700 hover:bg-red-100"
          onClick={() => load(kind, openOnly, page)}
        >
          {t("retry", { defaultValue: "Retry" })}
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { summary } = data;

  return (
    <div className="flex flex-col gap-5" data-testid="settlement-payments">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-4">
        <div className={cardCls}>
          <span className={labelCls}>
            {t("settlements_paid_in", { defaultValue: "Received" })}
          </span>
          <span className={valueCls}>
            <Currency value={summary.paidIn} isSymbol isDecimal />
          </span>
        </div>
        <div className={cardCls}>
          <span className={labelCls}>
            {t("settlements_paid_out", { defaultValue: "Paid" })}
          </span>
          <span className={valueCls}>
            <Currency value={summary.paidOut} isSymbol isDecimal />
          </span>
        </div>
        <div className={cardCls}>
          <span className={labelCls}>
            {t("settlements_advance_received", {
              defaultValue: "Advances received",
            })}
          </span>
          <span className={valueCls}>
            <Currency value={summary.advanceReceived} isSymbol isDecimal />
          </span>
          <span className="text-xs text-gray-500">
            {t("settlements_advance_received_hint", {
              defaultValue:
                "Not yet covered by a document — increases what we owe",
            })}
          </span>
        </div>
        <div className={cardCls}>
          <span className={labelCls}>
            {t("settlements_advance_paid", { defaultValue: "Advances paid" })}
          </span>
          <span className={valueCls}>
            <Currency value={summary.advancePaid} isSymbol isDecimal />
          </span>
          <span className="text-xs text-gray-500">
            {t("settlements_advance_paid_hint", {
              defaultValue:
                "Not yet covered by a document — increases what they owe",
            })}
          </span>
        </div>
      </div>

      {!summary.isSettlementsBased ? (
        <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-500">
          {t("settlements_payments_not_synced_yet", {
            defaultValue:
              "Payments appear once this company's first full payment import has finished.",
          })}
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <div
              className="flex flex-wrap gap-1.5"
              role="group"
              aria-label={t("kind")}
            >
              {KIND_FILTERS.map((option) => (
                <button
                  key={option.value || "all"}
                  type="button"
                  className={clsx(
                    "rounded-full px-3 py-1 text-sm transition-colors max-md:py-3",
                    kind === option.value
                      ? "bg-gray-900 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                  onClick={() => {
                    setKind(option.value);
                    setPage(1);
                  }}
                >
                  {t(option.i18nKey, { defaultValue: option.defaultValue })}
                </button>
              ))}
            </div>

            <Field className="flex items-center gap-2 max-md:min-h-11">
              <Switch
                checked={openOnly}
                onChange={(value: boolean) => {
                  setOpenOnly(value);
                  setPage(1);
                }}
                className={clsx(
                  openOnly ? "bg-gray-900" : "bg-gray-200",
                  "relative inline-flex h-5 w-9 items-center rounded-full transition-colors max-md:h-6 max-md:w-11"
                )}
              >
                <span
                  className={clsx(
                    openOnly ? "translate-x-5 max-md:translate-x-6" : "translate-x-1",
                    "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform max-md:h-4 max-md:w-4"
                  )}
                />
              </Switch>
              <Label className="text-sm text-gray-700">
                {t("settlements_only_open_advances", {
                  defaultValue: "Only open advances",
                })}
              </Label>
            </Field>
          </div>

          <PaymentsTable
            key={`${kind}-${openOnly}-${data.page}`}
            items={data.items}
            total={data.total}
            page={data.page}
            pageSize={data.pageSize}
            loading={loading}
            t={t}
            onPrev={() => setPage((current) => Math.max(1, current - 1))}
            onNext={() =>
              setPage((current) =>
                current * data.pageSize < data.total ? current + 1 : current
              )
            }
          />
        </>
      )}
    </div>
  );
}

function PaymentsTable({
  items,
  total,
  page,
  pageSize,
  loading,
  t,
  onPrev,
  onNext,
}: {
  items: CustomerSettlementItem[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  t: TFunction;
  onPrev: () => void;
  onNext: () => void;
}) {
  const isPhone = useIsPhone();
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const columnCount = 8; // expand chevron + the 7 data columns below

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div
      className={clsx(
        "overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm",
        loading && "opacity-60"
      )}
    >
      {isPhone ? (
        <PaymentCards items={items} expandedIds={expandedIds} onToggle={toggleExpanded} t={t} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500">
                <th className="w-8 px-2 py-2" aria-hidden="true" />
                <th className="px-3 py-2">
                  {t("ledger_col_date", { defaultValue: "Date" })}
                </th>
                <th className="px-3 py-2">
                  {t("ledger_col_kind", { defaultValue: "Type" })}
                </th>
                <th className="px-3 py-2">
                  {t("ledger_col_journal", { defaultValue: "Journal" })}
                </th>
                <th className="px-3 py-2">
                  {t("settlements_col_description", {
                    defaultValue: "Description",
                  })}
                </th>
                <th className="px-3 py-2 text-right">
                  {t("ledger_col_amount_eur", { defaultValue: "Amount EUR" })}
                </th>
                <th className="px-3 py-2 text-right">
                  {t("settlements_col_allocated", { defaultValue: "Allocated" })}
                </th>
                <th className="px-3 py-2 text-right">
                  {t("settlements_col_open", { defaultValue: "Advance" })}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-3 py-8 text-center text-gray-400"
                  >
                    {t("no_data", { defaultValue: "No data" })}
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  const isExpanded = expandedIds.has(item.id);
                  return (
                    <Fragment key={item.id}>
                      <tr className="border-b border-gray-100 last:border-b-0">
                        <td className="px-2 py-2">
                          <button
                            type="button"
                            aria-label={isExpanded ? t("collapse") : t("expand")}
                            aria-expanded={isExpanded}
                            className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-gray-100"
                            onClick={() => toggleExpanded(item.id)}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          {formatPrettyDate(item.opDate)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          {settlementKindLabel(t, item.kind)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          {item.journal || "-"}
                        </td>
                        <td
                          className="max-w-xs px-3 py-2 max-md:break-words md:truncate"
                          title={item.title || undefined}
                        >
                          {[item.number, item.title]
                            .filter(Boolean)
                            .join(" · ") || "-"}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right">
                          <Currency value={item.amountEur} isSymbol isDecimal />
                          {item.currency && item.currency !== "EUR" && (
                            <span className="ml-1 text-xs text-gray-400">
                              ({item.amountDoc} {item.currency})
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right text-gray-600">
                          <Currency
                            value={item.allocatedEur}
                            isSymbol
                            isDecimal
                          />
                        </td>
                        <td
                          className={clsx(
                            "whitespace-nowrap px-3 py-2 text-right",
                            item.openEur !== 0 && "font-medium text-amber-700"
                          )}
                        >
                          <Currency value={item.openEur} isSymbol isDecimal />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-b border-gray-100 bg-gray-50/70 last:border-b-0">
                          <td />
                          <td colSpan={columnCount - 1} className="px-3 py-2">
                            <PaymentAllocations item={item} t={t} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-gray-200 px-3 py-2 text-sm text-gray-600">
        <span>
          {total === 0
            ? t("no_data", { defaultValue: "No data" })
            : t("pagination_range", {
                defaultValue: "{{start}}-{{end}} of {{total}}",
                start,
                end,
                total,
              })}
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 max-md:h-11 max-md:w-11"
            onClick={onPrev}
            disabled={page <= 1}
            aria-label={t("desk_page_prev", { defaultValue: "Previous" })}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 max-md:h-11 max-md:w-11"
            onClick={onNext}
            disabled={end >= total}
            aria-label={t("desk_page_next", { defaultValue: "Next" })}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// The phone's version of PaymentsTable's rows (see PhoneRowCard).
function PaymentCards({
  items,
  expandedIds,
  onToggle,
  t,
}: {
  items: CustomerSettlementItem[];
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  t: TFunction;
}) {
  if (items.length === 0) {
    return (
      <p className="px-3 py-8 text-center text-sm text-gray-400">
        {t("no_data", { defaultValue: "No data" })}
      </p>
    );
  }

  return (
    <div>
      {items.map((item) => (
        <PhoneRowCard
          key={item.id}
          title={`${formatPrettyDate(item.opDate)} · ${settlementKindLabel(t, item.kind)}`}
          headline={<Currency value={item.amountEur} isSymbol isDecimal />}
          isExpanded={expandedIds.has(item.id)}
          onToggle={() => onToggle(item.id)}
          details={<PaymentAllocations item={item} t={t} />}
        >
          <div>{[item.number, item.title].filter(Boolean).join(" · ") || "-"}</div>
          {item.journal && <div>{item.journal}</div>}
          <div>
            {t("settlements_col_allocated", { defaultValue: "Allocated" })}:{" "}
            <Currency value={item.allocatedEur} isSymbol isDecimal />
          </div>
          <div className={clsx(item.openEur !== 0 && "font-medium text-amber-700")}>
            {t("settlements_col_open", { defaultValue: "Advance" })}:{" "}
            <Currency value={item.openEur} isSymbol isDecimal />
          </div>
        </PhoneRowCard>
      ))}
    </div>
  );
}

/** Which documents a payment was spread over (shown when its row is expanded). */
function PaymentAllocations({ item, t }: { item: CustomerSettlementItem; t: TFunction }) {
  const allocations = item.allocations || [];

  return (
    <>
      {allocations.length === 0 ? (
        <span className="text-xs text-gray-500">
          {t("settlements_no_allocations", {
            defaultValue:
              "Not spread over any document — the whole amount is an advance.",
          })}
        </span>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1 pr-3">
                {t("settlements_col_document", {
                  defaultValue: "Document",
                })}
              </th>
              <th className="py-1 pr-3">
                {t("ledger_col_date", {
                  defaultValue: "Date",
                })}
              </th>
              <th className="py-1 pr-3">
                {t("settlements_col_allocation_source", {
                  defaultValue: "Applied as",
                })}
              </th>
              <th className="py-1 text-right">
                {t("ledger_col_amount_eur", {
                  defaultValue: "Amount EUR",
                })}
              </th>
            </tr>
          </thead>
          <tbody>
            {allocations.map((allocation, index) => (
              <tr
                key={`${item.id}-${index}`}
                className="text-gray-700"
              >
                <td className="py-1 pr-3">
                  {allocation.documentNumber ||
                    [
                      allocation.documentJournal,
                      allocation.documentOpNumber,
                    ]
                      .filter(Boolean)
                      .join(" #") ||
                    "-"}
                  {allocation.documentKind && (
                    <span className="ml-1.5 text-gray-400">
                      {ledgerKindLabel(
                        t,
                        allocation.documentKind
                      )}
                    </span>
                  )}
                </td>
                <td className="py-1 pr-3">
                  {allocation.documentDate
                    ? formatPrettyDate(
                        allocation.documentDate
                      )
                    : "-"}
                </td>
                <td className="py-1 pr-3">
                  {allocation.source === "advance_ext"
                    ? t("settlements_source_advance", {
                        defaultValue: "Advance offset",
                      })
                    : t("settlements_source_payment", {
                        defaultValue: "Direct payment",
                      })}
                </td>
                <td className="py-1 text-right">
                  <Currency
                    value={allocation.amountEur}
                    isSymbol
                    isDecimal
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {item.allocationsTruncated && (
        <p className="mt-1 text-xs text-gray-400">
          {t("settlements_allocations_truncated", {
            defaultValue:
              "Only the first 200 allocations are shown.",
          })}
        </p>
      )}
    </>
  );
}
